import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/ui/layouts/CompanyMenu.tsx
import { useEffect, useRef, useState } from 'react';
import { Box, Checkbox, CircularProgress, Menu, MenuItem, ListSubheader, Divider, TextField, useTheme, ListItemIcon, ListItemText, Collapse, ListItem, alpha } from '@mui/material';
import { KeyboardArrowRight as ChevronRightIcon } from '@mui/icons-material';
import { useAppDispatch } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { getAllUserCompaniesAsync } from '@application/features/company/companyThunk';
import { setSelectedCompanyId } from '@application/features/company/companySlice';
import LocalStorageService from '@application/common/local-storage-service';
import { refreshTokenAsync } from '@application/features/auth/authThunk';
import { getAllPeopleForTasksAsync } from '@application/features/people/peopleThunk';
import { useTranslation } from 'react-i18next';
import { APIStatus } from '@application/enums/api-status';
const CompanyMenu = ({ anchorEl, open, onClose, onSelectCompany }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const inputRef = useRef(null);
    const companiesByRegion = useSelector((state) => state.company.companiesByRegion);
    const selectedCompanyId = useSelector((state) => state.company.selectedCompanyId);
    const companiesStatus = useSelector((state) => state.company.status);
    const isLoadingCompanies = companiesStatus === APIStatus.PENDING && companiesByRegion.length === 0;
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRegions, setExpandedRegions] = useState(new Set());
    const [collapsedRegionsDuringSearch, setCollapsedRegionsDuringSearch] = useState(new Set());
    // Fetch user companies
    useEffect(() => {
        dispatch(getAllUserCompaniesAsync());
    }, [dispatch]);
    // NOTE: Company selection is initialized centrally (Sidebar) to avoid conflicting defaults.
    // Filter regions and companies by search
    const filteredRegions = companiesByRegion
        .map((region) => ({
        ...region,
        companies: region.companies.filter((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
    }))
        .filter((r) => r.companies.length > 0);
    const filteredCompanies = filteredRegions.flatMap((region) => region.companies);
    useEffect(() => {
        setCollapsedRegionsDuringSearch(new Set());
    }, [searchTerm]);
    const toggleRegionExpand = (regionKey) => {
        const hasSearch = searchTerm.trim().length > 0;
        if (hasSearch) {
            setCollapsedRegionsDuringSearch((prev) => {
                const next = new Set(prev);
                const isAutoOpen = !next.has(regionKey);
                if (isAutoOpen)
                    next.add(regionKey);
                else
                    next.delete(regionKey);
                return next;
            });
            return;
        }
        setExpandedRegions((prev) => {
            const next = new Set(prev);
            if (next.has(regionKey)) {
                next.delete(regionKey);
            }
            else {
                next.clear();
                next.add(regionKey);
            }
            return next;
        });
    };
    const handleRegionToggle = (companyIds) => {
        if (!companyIds.length)
            return;
        const allSelected = companyIds.every((id) => selectedCompanyId.includes(id));
        let updatedSelection;
        if (allSelected) {
            updatedSelection = selectedCompanyId.filter((id) => !companyIds.includes(id));
        }
        else {
            const merged = [...selectedCompanyId, ...companyIds];
            updatedSelection = Array.from(new Set(merged));
        }
        dispatch(setSelectedCompanyId(updatedSelection));
        LocalStorageService.setSelectedCompanyIdForTab(updatedSelection);
        dispatch(refreshTokenAsync());
        dispatch(getAllPeopleForTasksAsync());
    };
    const handleSelectAllToggle = () => {
        const filteredCompanyIds = filteredCompanies.map((c) => c.id);
        const allFilteredSelected = filteredCompanyIds.every((id) => selectedCompanyId.includes(id));
        let updatedSelection;
        if (allFilteredSelected) {
            updatedSelection = selectedCompanyId.filter((id) => !filteredCompanyIds.includes(id));
        }
        else {
            const merged = [...selectedCompanyId, ...filteredCompanyIds];
            updatedSelection = Array.from(new Set(merged));
        }
        dispatch(setSelectedCompanyId(updatedSelection));
        LocalStorageService.setSelectedCompanyIdForTab(updatedSelection);
        dispatch(refreshTokenAsync());
        dispatch(getAllPeopleForTasksAsync());
    };
    // Render company menu items by region; within each region, selected first then unselected (alphabetically)
    const renderCompanyMenuItems = () => {
        return filteredRegions.map((region) => {
            const regionCompanyIds = region.companies.map((c) => c.id);
            const selectedInRegion = region.companies
                .filter((c) => selectedCompanyId.includes(c.id))
                .sort((a, b) => a.name.localeCompare(b.name));
            const unselectedInRegion = region.companies
                .filter((c) => !selectedCompanyId.includes(c.id))
                .sort((a, b) => a.name.localeCompare(b.name));
            const allSelected = regionCompanyIds.length > 0 &&
                regionCompanyIds.every((id) => selectedCompanyId.includes(id));
            const someSelected = !allSelected && regionCompanyIds.some((id) => selectedCompanyId.includes(id));
            return (_jsxs(Box, { sx: { flexShrink: 0 }, children: [_jsxs(ListItem, { sx: {
                            ...theme.typography.caption,
                            color: theme.palette.common.grey.dark,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            pr: 1,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.common.grey.main, 0.05)
                            }
                        }, onClick: (e) => {
                            e.stopPropagation();
                            toggleRegionExpand(region.regionKey);
                        }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(ChevronRightIcon, { sx: {
                                            fontSize: 16,
                                            transition: theme.transitions.create('transform', {
                                                duration: theme.transitions.duration.short
                                            }),
                                            transform: expandedRegions.has(region.regionKey) ||
                                                (searchTerm.trim().length > 0 &&
                                                    !collapsedRegionsDuringSearch.has(region.regionKey))
                                                ? 'rotate(90deg)'
                                                : 'rotate(0deg)'
                                        } }), region.regionLabel] }), _jsx(Checkbox, { size: 'small', checked: allSelected, indeterminate: someSelected, onClick: (e) => {
                                    e.stopPropagation();
                                    handleRegionToggle(regionCompanyIds);
                                } })] }), _jsx(Collapse, { in: expandedRegions.has(region.regionKey) ||
                            (searchTerm.trim().length > 0 && !collapsedRegionsDuringSearch.has(region.regionKey)), timeout: 'auto', unmountOnExit: true, sx: { display: 'block', width: '100%' }, children: _jsxs(Box, { sx: { width: '100%', overflowY: 'auto', maxHeight: '300px', scrollbarGutter: 'stable' }, children: [selectedInRegion.map((company) => (_jsxs(MenuItem, { onClick: (e) => {
                                        e.stopPropagation();
                                        onSelectCompany(company.id);
                                    }, selected: true, sx: { pl: 3, '&:hover': { backgroundColor: theme.palette.grey[50] } }, children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx(Checkbox, { checked: true, size: 'small', sx: { padding: 0 } }) }), _jsx(ListItemText, { primary: company.name, sx: {
                                                minWidth: 0,
                                                '& .MuiListItemText-primary': {
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    color: theme.palette.grey[800],
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }
                                            } })] }, company.id))), unselectedInRegion.map((company) => (_jsxs(MenuItem, { onClick: (e) => {
                                        e.stopPropagation();
                                        onSelectCompany(company.id);
                                    }, selected: false, sx: { pl: 3, '&:hover': { backgroundColor: theme.palette.grey[50] } }, children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx(Checkbox, { checked: false, size: 'small', sx: { padding: 0 } }) }), _jsx(ListItemText, { primary: company.name, sx: {
                                                minWidth: 0,
                                                '& .MuiListItemText-primary': {
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    color: theme.palette.grey[800],
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }
                                            } })] }, company.id)))] }) })] }, region.regionKey));
        });
    };
    return (_jsxs(Menu, { anchorEl: anchorEl, open: open, onClose: onClose, anchorOrigin: { vertical: 'top', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, disableAutoFocusItem: true, PaperProps: {
            sx: {
                mt: 0,
                ml: 1,
                width: 361,
                maxHeight: 422,
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                border: '1px solid rgba(0,0,0,0.08)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '& .MuiMenuItem-root': { px: 2 }
            }
        }, MenuListProps: {
            sx: {
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                height: '100%'
            }
        }, children: [_jsx(ListSubheader, { sx: {
                    fontSize: '12px',
                    fontWeight: 600,
                    color: theme.palette.grey[500],
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    lineHeight: '32px',
                    px: 2,
                    py: 0,
                    position: 'sticky',
                    top: 0,
                    backgroundColor: theme.palette.background.paper,
                    zIndex: 1,
                    flexShrink: 0
                }, children: t('common.selectGroup') }), _jsx(Divider, { sx: { mx: 1, my: 0.5, flexShrink: 0 } }), _jsxs(Box, { sx: {
                    mx: 2,
                    my: 1,
                    backgroundColor: theme.palette.background.paper,
                    zIndex: 1,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                }, children: [_jsx(TextField, { fullWidth: true, placeholder: t('common.searchCompanies'), variant: 'outlined', size: 'small', value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), inputRef: inputRef, onKeyDown: (e) => e.stopPropagation() }), filteredCompanies.length > 0 && (_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                        }, onClick: (e) => {
                            e.stopPropagation();
                            handleSelectAllToggle();
                        }, children: [_jsx(Checkbox, { checked: filteredCompanies.every((c) => selectedCompanyId.includes(c.id)), size: 'small', onClick: (e) => {
                                    e.stopPropagation();
                                    handleSelectAllToggle();
                                } }), _jsx(Box, { component: 'span', sx: { fontSize: '12px', color: theme.palette.text.secondary }, children: searchTerm
                                    ? `${t('actions.selectAll') || 'Select All'} (${filteredCompanies.length})`
                                    : t('actions.selectAll') || 'Select All' })] }))] }), _jsx(Box, { sx: {
                    overflowY: 'auto',
                    flexGrow: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    position: 'relative'
                }, children: isLoadingCompanies ? (_jsx(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 4
                    }, children: _jsx(CircularProgress, { size: 24 }) })) : (renderCompanyMenuItems()) })] }));
};
export default CompanyMenu;
